/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useRef, useCallback } from "react";
import { addAuthTokenToUrl } from "@/shared/utils/sseConnection";
import { API_CONFIG } from "@/infrastructure/config/api";

// Definir os tipos baseados nas especificações do backend
export interface StorageItem {
  id: string;
  name: string;
  description?: string;
  file_name: string;
  file_size: number;
  file_type: string;
  status: "stored" | "processing" | "embedded" | "error";
  knowledge_base_id: string;
  organization_id: string;
  product_id: string;
  category: string;
  created_at: string;
  updated_at: string;
  tool_logo_url?: string;
  type?: string;
  [key: string]: any; // Para campos adicionais
}

export interface EmbeddingProgress {
  id: string;
  chunksProcessed: number;
  totalChunks: number;
  percentComplete: number;
}

export interface StorageSSEEvent {
  type:
    | "storage_updated"
    | "storage_uploaded"
    | "storage_deleted"
    | "storage:item_updated"
    | "storage:item_deleted"
    | "storage:embedding_progress"
    // v2 event names (sem `storage:` prefix; sem `initial_data` — clients usam REST)
    | "file_uploaded"
    | "status_changed"
    | "ingestion_progress"
    | "embedding_progress"
    | "item_deleted"
    | "connection_established"
    | "initial_data"
    | "ping";
  data: {
    storage?: StorageItem;
    items?: StorageItem[]; // apenas para initial_data (legacy)
    total?: number; // apenas para initial_data (legacy)
    message?: string;
    // Para storage:item_updated (legacy) / status_changed (v2), os dados vêm diretamente
    id?: string;
    storageId?: string; // v2 events usam storageId no top-level
    knowledgeBaseId?: string;
    status?: string;
    // Para storage:embedding_progress (legacy)
    chunksProcessed?: number;
    totalChunks?: number;
    percentComplete?: number;
    [key: string]: any;
  };
}

interface UseStorageSSEOptions {
  knowledgeBaseId: string;
  enabled?: boolean;
}

interface UseStorageSSEReturn {
  items: StorageItem[];
  total: number;
  isConnected: boolean;
  error: string | null;
  reconnect: () => void;
  embeddingProgress: Map<string, EmbeddingProgress>;
}

export function useStorageSSE({
  knowledgeBaseId,
  enabled = true,
}: UseStorageSSEOptions): UseStorageSSEReturn {
  const [items, setItems] = useState<StorageItem[]>([]);
  /**
   * Ids presentes na lista, mantido em paralelo ao estado.
   *
   * Serve para saber, no momento da chamada, se um evento é item novo ou update
   * do que já está lá — o `total` depende dessa distinção. Não dá para ler isso
   * do updater do `setItems`: ele roda depois, no próximo render, então um valor
   * calculado lá dentro chegaria tarde para quem chamou.
   */
  const itemIdsRef = useRef<Set<string>>(new Set());
  const [total, setTotal] = useState<number>(0);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [embeddingProgress, setEmbeddingProgress] = useState<
    Map<string, EmbeddingProgress>
  >(new Map());

  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  // Função para atualizar ou adicionar um item
  /**
   * Faz merge no item existente ou insere no topo. Devolve `true` só quando
   * INSERIU: o `total` é incrementado a partir daí, e antes ele subia em todo
   * evento de upload — evento repetido ou reenviado inflava o contador acima do
   * tamanho real da lista.
   */
  const updateOrAddItem = useCallback((newItem: StorageItem | any): boolean => {
    // Extrair campos do objeto storage se presente (para eventos SSE)
    let processedItem = { ...newItem };
    if (newItem.storage && typeof newItem.storage === "object") {
      processedItem = {
        ...newItem.storage, // Extrair todos os campos do storage
        ...newItem, // Manter campos do nível superior
        storage: undefined, // Remover o objeto storage aninhado
      };
      delete processedItem.storage;
    }

    const inserted = !itemIdsRef.current.has(processedItem.id);
    itemIdsRef.current.add(processedItem.id);

    setItems((prevItems) => {
      const existingIndex = prevItems.findIndex(
        (item) => item.id === processedItem.id,
      );

      if (existingIndex >= 0) {
        // Atualizar item existente preservando todos os campos
        const existingItem = prevItems[existingIndex];
        const updatedItem = {
          ...existingItem, // Preservar campos existentes
          ...processedItem, // Sobrescrever com novos dados processados
        };

        const newItems = [...prevItems];
        newItems[existingIndex] = updatedItem;
        return newItems;
      } else {
        // Adicionar novo item no topo da lista
        return [processedItem, ...prevItems];
      }
    });
    return inserted;
  }, []);

  /**
   * Merge em item que JÁ está na lista; evento para id desconhecido é ignorado.
   *
   * O `status_changed` do v2 manda só `{storageId, status}`. Passando pelo
   * `updateOrAddItem`, um id que a lista ainda não tem virava uma linha nova com
   * `id` e `status` e nada mais — sem nome, sem tamanho — no topo da lista. Quem
   * chega depois vem pelo `file_uploaded`, que traz o objeto completo.
   */
  const updateExistingItem = useCallback(
    (itemId: string, patch: Partial<StorageItem>) => {
      setItems((prevItems) => {
        const index = prevItems.findIndex((item) => item.id === itemId);
        if (index < 0) return prevItems;
        const newItems = [...prevItems];
        newItems[index] = { ...prevItems[index], ...patch };
        return newItems;
      });
    },
    [],
  );

  // Função para remover item
  const removeItem = useCallback((itemId: string) => {
    itemIdsRef.current.delete(itemId);
    setItems((prevItems) => prevItems.filter((item) => item.id !== itemId));
  }, []);

  // Função para conectar ao SSE
  const connect = useCallback(() => {
    if (!enabled || !knowledgeBaseId) {
      return;
    }

    // Fechar conexão existente se houver
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      const baseUrl = `${API_CONFIG.API_ROOT_URL}/knowledge-bases/${knowledgeBaseId}/storage/stream`;

      // Adicionar token de autenticação
      const url = addAuthTokenToUrl(baseUrl);

      // Criar EventSource
      const eventSource = new EventSource(url);

      eventSourceRef.current = eventSource;

      // Handler para abertura da conexão
      eventSource.onopen = () => {
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
      };

      // Handler para mensagens
      eventSource.onmessage = (event) => {
        try {
          const eventData: StorageSSEEvent = JSON.parse(event.data);

          switch (eventData.type) {
            case "connection_established":
              break;

            case "initial_data":
              if (eventData.data && eventData.data.items) {
                itemIdsRef.current = new Set(
                  (eventData.data.items as StorageItem[]).map(
                    (item) => item.id,
                  ),
                );
                setItems(eventData.data.items);
                setTotal(eventData.data.total || eventData.data.items.length);
              }
              break;

            case "storage_updated":
              if (eventData.data.storage) {
                updateOrAddItem(eventData.data.storage);
              }
              break;

            case "storage_uploaded":
              if (eventData.data.storage) {
                if (updateOrAddItem(eventData.data.storage)) {
                  setTotal((prev) => prev + 1);
                }
              }
              break;

            case "storage_deleted":
              if (eventData.data.storage) {
                removeItem(eventData.data.storage.id);
                setTotal((prev) => Math.max(0, prev - 1));
              }
              break;

            case "storage:item_deleted":
              if (eventData.data && eventData.data.id) {
                removeItem(eventData.data.id);
                setTotal((prev) => Math.max(0, prev - 1));
              }
              break;

            case "storage:item_updated":
              if (eventData.data && eventData.data.id) {
                updateOrAddItem(eventData.data as StorageItem);
                // Limpar progresso quando o item não está mais processando
                if (
                  eventData.data.status &&
                  eventData.data.status !== "processing"
                ) {
                  setEmbeddingProgress((prev) => {
                    const next = new Map(prev);
                    next.delete(eventData.data.id!);
                    return next;
                  });
                }
              }
              break;

            case "storage:embedding_progress":
              if (eventData.data && eventData.data.id) {
                setEmbeddingProgress((prev) => {
                  const next = new Map(prev);
                  // Atualização monotônica: nunca regride. Evita o flash visual
                  // de 100% → 0% caso o backend emita um evento de progresso
                  // posterior com valor menor (ou falsy → 0 pelo `||`).
                  const current = prev.get(eventData.data.id!);
                  const incomingPercent = eventData.data.percentComplete ?? 0;
                  const incomingChunks = eventData.data.chunksProcessed ?? 0;
                  next.set(eventData.data.id!, {
                    id: eventData.data.id!,
                    chunksProcessed: Math.max(
                      current?.chunksProcessed ?? 0,
                      incomingChunks,
                    ),
                    totalChunks:
                      eventData.data.totalChunks || current?.totalChunks || 0,
                    percentComplete: Math.max(
                      current?.percentComplete ?? 0,
                      incomingPercent,
                    ),
                  });
                  return next;
                });
              }
              break;

            // ---- v2 events ----
            case "file_uploaded":
              if (eventData.data.storage) {
                if (updateOrAddItem(eventData.data.storage)) {
                  setTotal((prev) => prev + 1);
                }
              }
              break;

            case "status_changed": {
              // v2 payload: {knowledgeBaseId, storageId, status}. UI faz merge no item
              // existente (mantém file_name/etc); refetch via REST se shape completo
              // for necessário.
              const sid = eventData.data.storageId;
              if (sid) {
                updateExistingItem(sid, {
                  status: eventData.data.status,
                } as Partial<StorageItem>);
                if (
                  eventData.data.status &&
                  eventData.data.status !== "processing"
                ) {
                  setEmbeddingProgress((prev) => {
                    const next = new Map(prev);
                    next.delete(sid);
                    return next;
                  });
                }
              }
              break;
            }

            case "embedding_progress": {
              // v2 progresso em tempo real (chunks). Mesma lógica monotônica do
              // legacy `storage:embedding_progress`, mas keyed por `storageId`.
              const sid = eventData.data.storageId;
              if (sid) {
                setEmbeddingProgress((prev) => {
                  const next = new Map(prev);
                  const current = prev.get(sid);
                  const incomingPercent = eventData.data.percentComplete ?? 0;
                  const incomingChunks = eventData.data.chunksProcessed ?? 0;
                  next.set(sid, {
                    id: sid,
                    chunksProcessed: Math.max(
                      current?.chunksProcessed ?? 0,
                      incomingChunks,
                    ),
                    totalChunks:
                      eventData.data.totalChunks || current?.totalChunks || 0,
                    percentComplete: Math.max(
                      current?.percentComplete ?? 0,
                      incomingPercent,
                    ),
                  });
                  return next;
                });
              }
              break;
            }

            case "ingestion_progress": {
              // v2 synthetic completion signal (sem números) — limpa progress entry.
              // UI mostra status final via list refetch ou status_changed subsequent.
              const sid = eventData.data.storageId;
              if (sid) {
                setEmbeddingProgress((prev) => {
                  const next = new Map(prev);
                  next.delete(sid);
                  return next;
                });
              }
              break;
            }

            case "item_deleted": {
              const sid = eventData.data.storageId;
              if (sid) {
                removeItem(sid);
                setTotal((prev) => Math.max(0, prev - 1));
              }
              break;
            }

            case "ping":
              // Manter conexão viva
              break;

            default:
              console.warn("[useStorageSSE] Unknown event:", eventData.type);
          }
        } catch (err) {
          // Só um trecho do payload: o corpo do evento carrega metadado de negócio
          // (nome de arquivo, campos do documento) e o console do navegador não é
          // lugar para isso. O trecho basta para reconhecer o formato quebrado.
          console.error("[useStorageSSE] Error parsing event data:", err, {
            payloadPreview:
              typeof event.data === "string"
                ? event.data.slice(0, 120)
                : undefined,
          });
        }
      };

      // Handler para erros
      eventSource.onerror = () => {
        console.error("[useStorageSSE] Erro de conexão:", {
          readyState: eventSource.readyState,
          retryCount: reconnectAttemptsRef.current,
          maxRetries: maxReconnectAttempts,
        });

        setIsConnected(false);

        if (eventSource.readyState === EventSource.CLOSED) {
          // Tentar reconectar com backoff exponencial
          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            reconnectAttemptsRef.current++;
            const delay = Math.min(
              Math.pow(2, reconnectAttemptsRef.current - 1) * 1000,
              30000,
            );

            setError(
              `Connection lost, retrying (${reconnectAttemptsRef.current}/${maxReconnectAttempts})...`,
            );

            reconnectTimeoutRef.current = setTimeout(() => {
              connect();
            }, delay);
          } else {
            setError("Unable to establish connection after multiple attempts");
          }
        }
      };
    } catch (err) {
      console.error("[useStorageSSE] Error creating EventSource:", err);
      setError("Failed to establish connection");
      setIsConnected(false);
    }
  }, [
    enabled,
    knowledgeBaseId,
    updateOrAddItem,
    updateExistingItem,
    removeItem,
  ]);

  // Função para reconectar manualmente
  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    setError(null);
    connect();
  }, [connect]);

  // Efeito para conectar/desconectar
  useEffect(() => {
    if (enabled && knowledgeBaseId) {
      connect();
    }

    // Cleanup
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      setIsConnected(false);
    };
  }, [enabled, knowledgeBaseId, connect]);

  // Cleanup no unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  return {
    items,
    total,
    isConnected,
    error,
    reconnect,
    embeddingProgress,
  };
}
